import React from "react";
import {
  EAutoFocusMethod,
  ELightPath,
  FocusCaptureProperty,
  PlateTypeCaptureProfile,
} from "../../../Model/CaptureProfile";
import { LightPathCapability } from "../../../Model/ImagerCapabilities";
import AutoFocusOptions from "./AutoFocusOptions";
import AutoFocusRangeOptions from "./AutoFocusRangeOptions";
import DefaultImagingSettingComboBox, { DisplayValue } from "./DefaultImagingSettingComboBox";
import DefaultImagingSettingTextBox from "./DefaultImagingSettingTextBox";
import FixedRangeDofLimitedOptions from "./FixedRangeDofLimitedOptions";
import FixedRangeOptions from "./FixedRangeOptions";

const AUTO_FOCUS_WITH_LIMITS = 1;
const FIXED_RANGE_DOF = 2;
const FIXED_RANGE_INTERPOLATED = 3;
const AUTO_FOCUS_METHOD = "autoFocusMethod";
const STEP_SIZE = "stepSize";

function getCaptureRangeDisplayValues(isVisible: boolean, isVisibleUseAutoFocus: boolean): DisplayValue[] {
  let captureRangeDisplayValues: DisplayValue[] = [];

  if (isVisible || isVisibleUseAutoFocus) {
    captureRangeDisplayValues.push({
      display: "Auto Focus With Limits",
      value: AUTO_FOCUS_WITH_LIMITS.toString(),
    });
  }
  if (isVisible || (!isVisible && !isVisibleUseAutoFocus)) {
    captureRangeDisplayValues.push({
      display: "Fixed Range (DoF Limited)",
      value: FIXED_RANGE_DOF.toString(),
    });

    captureRangeDisplayValues.push({
      display: "Fixed Range (Interpolated)",
      value: FIXED_RANGE_INTERPOLATED.toString(),
    });
  }

  return captureRangeDisplayValues;
}

type Props = {
  style?: React.CSSProperties;
  lightPath: ELightPath;
  otherCaptureProfiles: PlateTypeCaptureProfile[];
  captureProfile: PlateTypeCaptureProfile;
  setCaptureProfile: (captureProfile: PlateTypeCaptureProfile) => void;
  lighPathCapability: LightPathCapability;
  imagerIdentifier: string;
  onErrorOccur: (key: string) => void;
  onErrorCleared: (key: string) => void;
};

export default function FocusLevelOptions({
  style = {},
  imagerIdentifier,
  lightPath,
  otherCaptureProfiles,
  captureProfile,
  setCaptureProfile,
  lighPathCapability,
  onErrorCleared,
  onErrorOccur,
}: Props) {
  const isVisible = lightPath === ELightPath.Visible;
  const focusCaptureProperty = captureProfile.focus;
  const autoFocusAlgorithmProperty = captureProfile.visible?.autoFocus;

  if (focusCaptureProperty == null) {
    throw new Error("Capture profile focus is null");
  }

  if (isVisible && autoFocusAlgorithmProperty == null) {
    throw new Error("Auto focus algorithm property is null");
  }

  const onFocusCapturePropertyChanged = (focusCaptureProperty: FocusCaptureProperty): void => {
    setCaptureProfile({ ...captureProfile, focus: focusCaptureProperty });
  };

  const isVisibleUseAutoFocus = focusCaptureProperty.autoFocusMethod === EAutoFocusMethod.AutoFocusWithLimits;

  if (isVisible && isVisibleUseAutoFocus) {
    for (var nonVisibleCaptureProfile of otherCaptureProfiles) {
      if (nonVisibleCaptureProfile.focus?.autoFocusMethod !== EAutoFocusMethod.AutoFocusWithLimits) {
        if (nonVisibleCaptureProfile.focus == null) {
          continue;
        }
        setCaptureProfile({
          ...nonVisibleCaptureProfile,
          focus: {
            ...nonVisibleCaptureProfile.focus,
            autoFocusMethod: EAutoFocusMethod.AutoFocusWithLimits,
          },
        });
      }
    }
  }

  if (isVisible && !isVisibleUseAutoFocus) {
    for (var nonVisCaptureProfile of otherCaptureProfiles) {
      if (nonVisCaptureProfile.focus?.autoFocusMethod === EAutoFocusMethod.AutoFocusWithLimits) {
        if (nonVisCaptureProfile.focus == null) {
          continue;
        }
        setCaptureProfile({
          ...nonVisCaptureProfile,
          focus: {
            ...nonVisCaptureProfile.focus,
            autoFocusMethod: EAutoFocusMethod.FixedRangeInterpolated,
          },
        });
      }
    }
  }

  const captureRangeDisplayValues = getCaptureRangeDisplayValues(isVisible, isVisibleUseAutoFocus);

  return (
    <div
      style={{
        overflow: "auto",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        ...style,
      }}
    >
      <span
        style={{
          margin: "24px 0px",
          fontWeight: "bold",
        }}
      >
        Focus Level Capture Method
      </span>

      <DefaultImagingSettingComboBox
        displayValues={captureRangeDisplayValues}
        value={focusCaptureProperty.autoFocusMethod.toString()}
        onChange={(name, value) =>
          onFocusCapturePropertyChanged({
            ...focusCaptureProperty,
            autoFocusMethod: Number.parseInt(value),
          })
        }
        label="Capture Range"
        name={AUTO_FOCUS_METHOD}
        unit=""
        style={{
          paddingLeft: "13px",
          width: "100%",
        }}
        disabled={false}
      />

      {focusCaptureProperty.autoFocusMethod === EAutoFocusMethod.AutoFocusWithLimits && (
        <AutoFocusOptions
          focusCaptureProperty={focusCaptureProperty}
          onChange={onFocusCapturePropertyChanged}
          style={{
            paddingLeft: "13px",
            maxWidth: "100%",
          }}
          onErrorOccur={onErrorOccur}
          onErrorCleared={onErrorCleared}
        />
      )}

      {focusCaptureProperty.autoFocusMethod === EAutoFocusMethod.FixedRangeInterpolated && (
        <FixedRangeOptions
          fixedRangeInterpolatedCaptureProperty={focusCaptureProperty.fixedRangeInterpolated}
          onChange={(fixedRangeInterpolated) =>
            onFocusCapturePropertyChanged({ ...focusCaptureProperty, fixedRangeInterpolated: fixedRangeInterpolated })
          }
          style={{
            paddingLeft: "13px",
            maxWidth: "100%",
          }}
          onErrorCleared={onErrorCleared}
          onErrorOccur={onErrorOccur}
        />
      )}

      {focusCaptureProperty.autoFocusMethod === EAutoFocusMethod.FixedRangeDoF && (
        <FixedRangeDofLimitedOptions
          imagerIdentifier={imagerIdentifier}
          lightPath={lightPath}
          focusCaptureProperty={focusCaptureProperty}
          lighPathCapability={lighPathCapability}
          captureProfile={captureProfile}
          fixedRangeDofCaptureProperty={focusCaptureProperty.fixedRangeDofLimited}
          onChange={(fixedRangeDofLimited) =>
            onFocusCapturePropertyChanged({ ...focusCaptureProperty, fixedRangeDofLimited: fixedRangeDofLimited })
          }
          style={{
            paddingLeft: "13px",
            maxWidth: "100%",
          }}
          onErrorCleared={onErrorCleared}
          onErrorOccur={onErrorOccur}
        />
      )}

      <span
        style={{
          margin: "24px 0px",
          fontWeight: "bold",
        }}
      >
        Capture Levels
      </span>

      <DefaultImagingSettingTextBox
        value={focusCaptureProperty.depthOfFieldStepSizePercentage * 100}
        onChange={(name, value) =>
          onFocusCapturePropertyChanged({ ...focusCaptureProperty, depthOfFieldStepSizePercentage: value / 100 })
        }
        label="Step Size (Dof %)"
        name={STEP_SIZE}
        unit=""
        disabled={false}
        isInteger={false}
        min={1}
        max={100}
        style={{
          marginLeft: 13,
        }}
        onErrorOccur={(name) => onErrorOccur(name)}
        onErrorCleared={(name) => onErrorCleared(name)}
      />

      {isVisible && autoFocusAlgorithmProperty != null && (
        <>
          <div
            style={{
              margin: "24px 0px",
              fontWeight: "bold",
            }}
          >
            Auto Focus Capture Range
          </div>

          <AutoFocusRangeOptions
            autoFocusAlgorithmProperty={autoFocusAlgorithmProperty}
            onChange={(autoFocusAlgorithmProperty) =>
              setCaptureProfile({
                ...captureProfile,
                visible: { ...captureProfile.visible, autoFocus: autoFocusAlgorithmProperty },
              })
            }
            style={{
              paddingLeft: "13px",
              maxWidth: "100%",
            }}
            onErrorCleared={onErrorCleared}
            onErrorOccur={onErrorOccur}
          />
        </>
      )}
    </div>
  );
}
